import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, MessageCircle, Flag, Send, Plus, Users, Lock, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

function PostCard({ post, onChange }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.likes);

  const toggleLike = async () => {
    const { data } = await api.post(`/community/react`, { target_type: "post", target_id: post.id });
    setLiked(data.liked); setLikes((l) => l + (data.liked ? 1 : -1));
  };
  const loadComments = async () => { const { data } = await api.get(`/community/posts/${post.id}/comments`); setComments(data); };
  const toggle = () => { if (!expanded) loadComments(); setExpanded(!expanded); };
  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/community/posts/${post.id}/comments`, { body: comment });
    setComment(""); loadComments();
  };
  const report = async () => {
    const reason = window.prompt(t("community.reportReason"));
    if (reason) { await api.post("/community/report", { target_type: "post", target_id: post.id, reason }); toast.success(t("community.reported")); }
  };

  return (
    <div className="rounded-2xl border bg-card p-5" data-testid="post-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">{(post.author?.name || "U").slice(0, 2).toUpperCase()}</div>
        <div><div className="font-medium text-sm">{post.author?.name}</div>
          {post.group && <div className="text-xs text-muted-foreground">{post.group.name}</div>}</div>
      </div>
      {post.title && <div className="font-display font-semibold mb-1">{post.title}</div>}
      <p className="text-sm whitespace-pre-wrap">{post.body}</p>
      <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
        <button onClick={toggleLike} className={`flex items-center gap-1.5 ${liked ? "text-primary" : ""}`} data-testid="like-btn"><Heart className={`h-4 w-4 ${liked ? "fill-primary" : ""}`} />{likes}</button>
        <button onClick={toggle} className="flex items-center gap-1.5" data-testid="comment-toggle"><MessageCircle className="h-4 w-4" />{post.comment_count}</button>
        <button onClick={report} className="flex items-center gap-1.5 ml-auto hover:text-destructive" data-testid="report-btn"><Flag className="h-4 w-4" /></button>
      </div>
      {expanded && (
        <div className="mt-4 pt-4 border-t space-y-3" data-testid="comments-section">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2 text-sm" data-testid="comment-item">
              <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-xs shrink-0">{(c.author?.name || "U").slice(0, 2).toUpperCase()}</div>
              <div><span className="font-medium">{c.author?.name}</span> <span className="text-muted-foreground">{c.body}</span></div>
            </div>
          ))}
          <div className="flex gap-2">
            <Input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder={t("community.addComment")} data-testid="comment-input" />
            <Button size="icon" className="rounded-full shrink-0" onClick={addComment} data-testid="comment-submit"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FeedTab({ groups }) {
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);
  const [body, setBody] = useState("");
  const [groupId, setGroupId] = useState("none");
  const load = () => api.get("/community/feed").then(({ data }) => setPosts(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const publish = async () => {
    if (!body.trim()) return;
    await api.post("/community/posts", { body, group_id: groupId === "none" ? null : groupId });
    setBody(""); load();
  };
  const myGroups = groups.filter((g) => g.is_member);
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="rounded-2xl border bg-card p-5 space-y-3">
        <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("community.whatsOnMind")} data-testid="new-post-input" />
        <div className="flex items-center gap-2">
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger className="w-48" data-testid="post-group-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("community.feed")}</SelectItem>
              {myGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="rounded-full ml-auto" onClick={publish} data-testid="publish-post-btn">{t("community.publish")}</Button>
        </div>
      </div>
      {posts.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="no-posts">{t("community.noPosts")}</p>}
      {posts.map((p) => <PostCard key={p.id} post={p} onChange={load} />)}
    </div>
  );
}

function GroupsTab({ groups, reload }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", theme: "general", visibility: "public" });
  const create = async () => {
    if (!form.name.trim()) return;
    await api.post("/community/groups", form); setOpen(false); setForm({ name: "", description: "", theme: "general", visibility: "public" }); reload();
  };
  const join = async (id) => { await api.post(`/community/groups/${id}/join`); reload(); };
  const leave = async (id) => { await api.post(`/community/groups/${id}/leave`); reload(); };
  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-group-btn"><Plus className="h-4 w-4 mr-1" />{t("community.newGroup")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("community.newGroup")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("community.groupName")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name-input" /></div>
              <div><Label>{t("cv.description")}</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>{t("community.visibility")}</Label>
                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                  <SelectTrigger data-testid="group-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="public">{t("community.public")}</SelectItem><SelectItem value="private">{t("community.private")}</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="create-group-confirm">{t("common.create")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {groups.map((g) => (
          <div key={g.id} className="rounded-2xl border bg-card p-5" data-testid="group-card">
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Users className="h-5 w-5" /></div>
              {g.visibility === "private" ? <Lock className="h-4 w-4 text-muted-foreground" /> : <Globe className="h-4 w-4 text-muted-foreground" />}
            </div>
            <h3 className="font-display font-semibold mt-3">{g.name}</h3>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{g.description}</p>
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-muted-foreground">{g.member_count} {t("community.members")}</span>
              {g.is_member ? <Button size="sm" variant="outline" className="rounded-full" onClick={() => leave(g.id)} data-testid="leave-group">{t("community.leave")}</Button>
                : <Button size="sm" className="rounded-full" onClick={() => join(g.id)} data-testid="join-group">{t("community.join")}</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Community() {
  const { t } = useI18n();
  const [groups, setGroups] = useState([]);
  const loadGroups = () => api.get("/community/groups").then(({ data }) => setGroups(data)).catch(() => {});
  useEffect(() => { loadGroups(); }, []);
  return (
    <div>
      <PageHeader title={t("community.title")} subtitle={t("community.subtitle")} />
      <Tabs defaultValue="feed">
        <TabsList className="mb-6">
          <TabsTrigger value="feed" data-testid="tab-feed"><MessageCircle className="h-4 w-4 mr-1.5" />{t("community.feed")}</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups"><Users className="h-4 w-4 mr-1.5" />{t("community.groups")}</TabsTrigger>
        </TabsList>
        <TabsContent value="feed"><FeedTab groups={groups} /></TabsContent>
        <TabsContent value="groups"><GroupsTab groups={groups} reload={loadGroups} /></TabsContent>
      </Tabs>
    </div>
  );
}
